// CSV and JSON → a chart table (TODO §15/§4.3, §4.5, §5.1).
//
// PapaParse is the only mature browser CSV parser that handles quoting, embedded newlines and
// worker-based streaming, and the last of those is why it is here rather than an extension of
// `parseClipboardTable`: a 10 MB file parsed on the main thread freezes the editor for several
// seconds, and §13.7 asks for a 5 MB import that does not (§4.3).
//
// **It is imported dynamically.** A deck that never imports a CSV must not download a parser,
// and the same door means the worker build is fetched once and shared.

import {
  CHART_ROW_CAP,
  inferColumnType,
  newColumn,
  parseCell,
} from "@/lib/chart";
import type { ChartColumn, ChartRow } from "@/types/chart";

// §4.3 Above this, parse in a worker. Below it the worker's own startup cost is the larger
// number, and the main thread was never going to be blocked long enough to see.
const WORKER_THRESHOLD_BYTES = 1_000_000;

// What a parse produced, plus what it had to leave behind. The second half is the point of
// the type: §4.5 requires an import to REPORT what it dropped rather than silently truncate,
// and a function that returns only rows has nowhere to say it.
export interface ParsedTable {
  columns: ChartColumn[];
  rows: ChartRow[];
  // Human-readable notes for the toast — "3 rows could not be read", "capped at 5000 rows".
  // Empty on a clean import, which is the common case and shows no toast body at all.
  notes: string[];
}

// A header row → columns. Blank headers get positional names rather than an empty label,
// which would render as a nameless series in the legend.
function columnsFrom(headers: string[]): ChartColumn[] {
  return headers.map((header, i) =>
    newColumn(header.trim() || `Column ${i + 1}`),
  );
}

// Records (objects keyed by header text) → our rows, keyed by column ID. This is where the
// header→id indirection §3.2.1 exists for is actually established.
function rowsFrom(
  records: Record<string, unknown>[],
  headers: string[],
  columns: ChartColumn[],
): { rows: ChartRow[]; capped: boolean } {
  const rows: ChartRow[] = [];
  const capped = records.length > CHART_ROW_CAP;
  for (const record of records.slice(0, CHART_ROW_CAP)) {
    const row: ChartRow = {};
    headers.forEach((header, i) => {
      const raw = record[header];
      row[columns[i].id] =
        typeof raw === "number"
          ? raw
          : raw == null
            ? null
            : parseCell(String(raw));
    });
    rows.push(row);
  }
  return { rows, capped };
}

// Give every column the type its own values imply, now that the rows exist. Declared types are
// what the axis and the encoding read, so getting them from the data rather than from a guess
// is what makes an imported table plot without anyone opening the panel.
function typed(columns: ChartColumn[], rows: ChartRow[]): ChartColumn[] {
  return columns.map((column) => ({
    ...column,
    type: inferColumnType(rows.map((r) => r[column.id])),
  }));
}

export async function parseCsvFile(file: File): Promise<ParsedTable> {
  const { default: Papa } = await import("papaparse");
  return new Promise<ParsedTable>((resolve, reject) => {
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      // §4.3 Off the main thread for anything big. `worker: true` with `header: true` still
      // delivers one `complete` with every row, so nothing downstream changes shape.
      worker: file.size > WORKER_THRESHOLD_BYTES,
      complete: (result) => {
        const headers = result.meta.fields ?? [];
        if (headers.length === 0) {
          reject(new Error("No columns found — is this a CSV file?"));
          return;
        }
        const columns = columnsFrom(headers);
        const { rows, capped } = rowsFrom(result.data, headers, columns);
        const notes: string[] = [];
        // PapaParse reports per-row problems rather than failing: a file with three broken
        // lines still imports, and the count is what the user is told (§4.5).
        if (result.errors.length > 0) {
          notes.push(
            `${result.errors.length} row${result.errors.length === 1 ? "" : "s"} could not be read and ${result.errors.length === 1 ? "was" : "were"} skipped.`,
          );
        }
        if (capped) {
          notes.push(
            `Kept the first ${CHART_ROW_CAP.toLocaleString()} rows; the file has more.`,
          );
        }
        resolve({ columns: typed(columns, rows), rows, notes });
      },
      error: (err: Error) => reject(err),
    });
  });
}

// The same, for CSV text already in hand — what a URL source hands back (§5.1). No worker: the
// text has already been downloaded and buffered, so the streaming argument does not apply and
// a worker round-trip is pure overhead.
export async function parseCsvText(text: string): Promise<ParsedTable> {
  const { default: Papa } = await import("papaparse");
  const result = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    dynamicTyping: true,
    skipEmptyLines: true,
  });
  const headers = result.meta.fields ?? [];
  if (headers.length === 0) {
    throw new Error("The response had no CSV header row.");
  }
  const columns = columnsFrom(headers);
  const { rows, capped } = rowsFrom(result.data, headers, columns);
  const notes = capped
    ? [`Kept the first ${CHART_ROW_CAP.toLocaleString()} rows.`]
    : [];
  return { columns: typed(columns, rows), rows, notes };
}

// §5.1 A JSON response → a table. `jsonPath` is a dotted path to the array; absent means the
// response is itself the array. Array indices are accepted as path segments ("data.0.rows"),
// because an API that wraps its payload in a one-element list is common enough to be worth
// the three lines.
export function parseJsonTable(
  payload: unknown,
  jsonPath?: string,
): ParsedTable {
  let node: unknown = payload;
  for (const segment of (jsonPath ?? "").split(".").filter(Boolean)) {
    if (node == null || typeof node !== "object") {
      throw new Error(`No value at "${jsonPath}" in the response.`);
    }
    node = (node as Record<string, unknown>)[segment];
  }
  if (!Array.isArray(node)) {
    throw new Error(
      jsonPath
        ? `"${jsonPath}" is not a list of rows.`
        : "The response is not a list of rows — set a path to the array.",
    );
  }
  const records = node.filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === "object" && !Array.isArray(item),
  );
  if (records.length === 0) {
    throw new Error("The list has no objects in it.");
  }
  // The union of keys across every record, in first-seen order. Taking the first record's keys
  // alone loses a column that only appears further down, which is exactly what happens with
  // an API that omits null fields.
  const headers: string[] = [];
  for (const record of records) {
    for (const key of Object.keys(record)) {
      if (!headers.includes(key)) headers.push(key);
    }
  }
  const columns = columnsFrom(headers);
  const { rows, capped } = rowsFrom(records, headers, columns);
  const notes = capped
    ? [`Kept the first ${CHART_ROW_CAP.toLocaleString()} rows.`]
    : [];
  return { columns: typed(columns, rows), rows, notes };
}

// === §4.5 Mapping an import onto the columns already there ==================================
//
// An import into an EXISTING chart matches by header text where it can, so a re-import of the
// same report keeps the encoding, the series colours and the plotted set — the whole point of
// ids being stable (§3.2.1). Columns the file does not mention are dropped along with their
// series; columns it adds are appended.
//
// Match is case-insensitive and trimmed, because "Revenue" and "revenue " are the same column
// in every spreadsheet anyone actually exports.
export function mapOntoColumns(
  existing: ChartColumn[],
  parsed: ParsedTable,
): ParsedTable {
  const byLabel = new Map(
    existing.map((c) => [c.label.trim().toLowerCase(), c]),
  );
  let matched = 0;
  const columns = parsed.columns.map((column) => {
    const found = byLabel.get(column.label.trim().toLowerCase());
    if (!found) return column;
    matched++;
    // The existing ID, the file's TYPE: the type comes from the data that just arrived, and a
    // column that used to be text but is now numbers should plot.
    return { ...found, label: column.label, type: column.type };
  });
  if (matched === 0) return parsed;

  // Re-key the rows onto whichever id each column ended up with.
  const rows = parsed.rows.map((row) => {
    const next: ChartRow = {};
    parsed.columns.forEach((oldColumn, i) => {
      next[columns[i].id] = row[oldColumn.id] ?? null;
    });
    return next;
  });

  const dropped = existing.filter(
    (c) => !columns.some((next) => next.id === c.id),
  );
  const notes = [...parsed.notes];
  if (dropped.length > 0) {
    notes.push(
      `The file has no column named ${dropped.map((c) => `"${c.label}"`).join(", ")} — ${dropped.length === 1 ? "it was" : "they were"} removed.`,
    );
  }
  return { columns, rows, notes };
}
